import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadBlogPosts, loadCategories } from '../utils/content';
import FontAwesomeIcon from '../components/FontAwesomeIcon';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function loadContent() {
      try {
        const [blogPosts, blogCategories] = await Promise.all([
          loadBlogPosts(),
          loadCategories(),
        ]);
        setPosts(blogPosts);
        setCategories(blogCategories);
      } catch (error) {
        console.error('Error loading blog content:', error);
      } finally {
        setLoading(false);
      }
    }
    loadContent();
  }, []);

  const filteredPosts = posts.filter(post => {
    const matchesCategory =
      selectedCategory === 'all' ||
      (post.categories && post.categories.includes(selectedCategory));
    const matchesSearch =
      searchTerm === '' ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon
            icon="fa-spinner"
            className="w-12 h-12 text-safari-gold animate-spin mx-auto mb-4"
          />
          <p className="text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-safari-green to-safari-green/90 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-heading">
              Travel Stories & Safari Guides
            </h1>
            <p className="text-xl text-gray-100">
              Expert insights, travel tips, and inspiring stories from Uganda's wild heart
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-safari-light border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="w-full md:w-1/2">
                <div className="relative">
                  <FontAwesomeIcon
                    icon="fa-search"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="w-full md:w-auto flex gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-lg transition ${
                    selectedCategory === 'all'
                      ? 'bg-safari-green text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  All Articles
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg transition ${
                      selectedCategory === category
                        ? 'bg-safari-green text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <FontAwesomeIcon
                  icon="fa-newspaper"
                  className="w-16 h-16 text-gray-300 mx-auto mb-4"
                />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No articles found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <p className="text-gray-600">
                    Showing {filteredPosts.length}{' '}
                    {filteredPosts.length === 1 ? 'article' : 'articles'}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.map(post => (
                    <article
                      key={post.slug}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                      <Link to={`/blog/${post.slug}`}>
                        {post.featuredImage && (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={post.featuredImage}
                              alt={post.featuredImageAlt || post.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}

                        <div className="p-6">
                          {/* Categories */}
                          {post.categories && post.categories.length > 0 && (
                            <div className="flex gap-2 mb-3 flex-wrap">
                              {post.categories.slice(0, 2).map((cat, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-safari-green text-white px-2 py-1 rounded-full"
                                >
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Title */}
                          <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 hover:text-safari-gold transition">
                            {post.title}
                          </h2>

                          {/* Excerpt */}
                          {post.excerpt && (
                            <p className="text-gray-600 mb-4 line-clamp-3">
                              {post.excerpt}
                            </p>
                          )}

                          {/* Meta */}
                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon="fa-user" className="w-4 h-4" />
                              {post.author}
                            </span>
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon="fa-calendar" className="w-4 h-4" />
                              {new Date(post.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>

                          {/* Read More */}
                          <div className="flex items-center text-safari-gold font-semibold hover:text-safari-green transition">
                            Read Article
                            <FontAwesomeIcon
                              icon="fa-arrow-right"
                              className="w-4 h-4 ml-2"
                            />
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 font-heading">
            Ready to Start Your Uganda Adventure?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Let us create your perfect safari experience
          </p>
          <Link to="/contact" className="btn-primary inline-block">
            Plan Your Trip
          </Link>
        </div>
      </section>
    </div>
  );
}
